import { Card } from '@/components/ui/card';
import { Building2, TrendingUp, Users, CheckCircle2, Award, Target } from 'lucide-react';

export const ServicesSection = () => {
  const services = [
    {
      icon: Building2,
      title: 'Verkauf Ihrer Handelsimmobilie',
      description: 'Wir übernehmen den kompletten Verkaufsprozess: Von der professionellen Objektaufbereitung mit Drohnenfotos über die bundesweite Käuferansprache bis zur Verhandlung und Vertragsabwicklung.',
      highlights: [
        'Hochwertiges Exposé mit Drohnenaufnahmen',
        'Bundesweite Käuferansprache',
        'Professionelle Verhandlungsführung',
        'Transparente Kommunikation'
      ]
    },
    {
      icon: TrendingUp,
      title: 'Objektbewertung & Marktanalyse',
      description: 'Fundierte Bewertung Ihrer Immobilie mit spezialisierten Tools: WAULT-Berechnung, Retail Score-Analyse und Live-Marktpreisrechner für eine realistische Preisfindung.',
      highlights: [
        'WAULT-Analyse (Mietvertragslaufzeiten)',
        'Retail Score-Bewertung',
        'Marktpreis-Berechnung',
        'Verkaufsbereitschafts-Check'
      ]
    },
    {
      icon: Users,
      title: 'Direkter Zugang zu Investoren',
      description: 'Profitieren Sie von unserem Netzwerk: Über 500 institutionelle Investoren und Family Offices, die gezielt nach Handelsimmobilien suchen – deutschlandweit.',
      highlights: [
        '500+ Investoren im Netzwerk',
        'Institutionelle Käufer & Family Offices',
        'Gezielte Käufer-Matching-Services',
        'Diskrete Ansprache'
      ]
    }
  ];

  const stats = [
    { number: '12+', label: 'Erfolgreich vermittelte Objekte 2024', icon: Award },
    { number: '€4,2M', label: 'Durchschnittlicher Verkaufspreis', icon: TrendingUp },
    { number: '6 Monate', label: 'Durchschnittliche Vermarktungsdauer', icon: Target }
  ];

  const assetExamples = [
    { emoji: '🏪', name: 'Lebensmittelmärkte', examples: 'EDEKA, REWE, ALDI, Kaufland' },
    { emoji: '🛒', name: 'Discounter', examples: 'Lidl, Penny, Netto, Norma' },
    { emoji: '🏬', name: 'Fachmärkte', examples: 'BAUHAUS, Obi, MediaMarkt, Decathlon' },
    { emoji: '🏢', name: 'Fachmarktzentren', examples: 'Multi-Tenant-Objekte mit Ankermietern' }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-16">
          <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-6 mx-auto"></div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
            Was wir als Makler für Sie tun
          </h2>
          <p className="text-lg md:text-xl max-w-3xl mx-auto" style={{ color: 'var(--gray-700)' }}>
            <strong style={{ color: 'var(--navy-800)' }}>Spezialisiert. Strukturiert. Erfolgreich.</strong>
            <br />
            Wir begleiten Sie von der ersten Bewertung bis zum erfolgreichen Verkaufsabschluss.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <Card
                key={index}
                className="glass-card p-8 hover:shadow-2xl transition-all duration-500 group"
                data-testid={`service-card-${index}`}
              >
                {/* Icon */}
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300"
                  style={{
                    background: 'linear-gradient(135deg, #0F2744 0%, #2563A0 100%)'
                  }}
                >
                  <Icon className="h-8 w-8 text-white" />
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--navy-800)' }}>
                  {service.title}
                </h3>

                {/* Description */}
                <p className="text-base mb-6 leading-relaxed" style={{ color: 'var(--gray-700)' }}>
                  {service.description}
                </p>

                {/* Highlights */}
                <ul className="space-y-2">
                  {service.highlights.map((highlight, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle2 
                        className="h-5 w-5 flex-shrink-0 mt-0.5" 
                        style={{ color: 'var(--gold-600)' }} 
                      />
                      <span className="text-sm" style={{ color: 'var(--gray-700)' }}>
                        {highlight}
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>
            );
          })}
        </div>

        {/* Stats Bar */}
        <Card className="glass-card p-8 mb-16">
          <div className="grid md:grid-cols-3 gap-8 divide-y md:divide-y-0 md:divide-x divide-gray-200">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="pt-6 md:pt-0 md:px-8 text-center">
                  <Icon 
                    className="h-8 w-8 mx-auto mb-3" 
                    style={{ color: 'var(--gold-600)' }} 
                  />
                  <div 
                    className="text-4xl md:text-5xl font-bold mb-2"
                    style={{ color: 'var(--navy-900)' }}
                  >
                    {stat.number}
                  </div>
                  <div className="text-sm font-medium" style={{ color: 'var(--gray-600)' }}>
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Asset Classes Examples */}
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>
            Unsere Spezialisierung: Handelsimmobilien
          </h3>
          <p className="text-base mb-8 max-w-2xl mx-auto" style={{ color: 'var(--gray-700)' }}>
            Wir kennen die Besonderheiten dieser Asset-Klasse und verstehen, worauf Investoren achten.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {assetExamples.map((asset, index) => (
            <Card
              key={index}
              className="glass-card p-6 text-center hover:shadow-xl transition-all duration-300"
              data-testid={`asset-example-${index}`}
            >
              <div className="text-5xl mb-3">{asset.emoji}</div>
              <h4 className="text-lg font-bold mb-2" style={{ color: 'var(--navy-800)' }}>
                {asset.name}
              </h4>
              <p className="text-sm" style={{ color: 'var(--gray-600)' }}>
                {asset.examples}
              </p>
            </Card>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <a
            href="/ersteinschaetzung"
            className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white bg-gradient-to-r from-[#D4A853] to-[#E5B960] rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            data-testid="services-cta-button"
          >
            Jetzt kostenlose Ersteinschätzung erhalten
          </a>
        </div>
      </div>
    </section>
  );
};
